import { type ReactNode } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { decimalPlacesFor, formatCompactAmount, formatDecimalString } from '@/lib/money';

/**
 * Where successful transaction amounts pile up — a Pareto histogram.
 *
 * This follows the four rules in `charts.tsx` (theme tokens never colour literals, quiet axes, a
 * text equivalent for every chart, one styled tooltip surface). Four further decisions are
 * specific to this chart and are the ones worth arguing about:
 *
 * 1. **Horizontal bars, ordinal category axis, never re-sorted.** Bucket labels are money ranges
 *    (`1,000–2,000`), which are long; horizontal bars give them a full-width gutter and no
 *    rotation. The buckets arrive ordered low → high and stay that way top → bottom, because the
 *    axis is *ordinal* — the reader is looking for the shape of a distribution, and sorting by bar
 *    length would destroy the only thing the axis order is carrying.
 *
 * 2. **Two independent scales, which a Pareto is entitled to.** The measure axis is fitted to the
 *    largest bucket; the share axis keeps its own `0 → 100%`. This is not the dual-axis mistake.
 *    That mistake is two *unrelated* units sharing a plot, aligned by nothing, asserting a
 *    correspondence the data does not contain. Here the line is the running sum of the very bars it
 *    crosses: the relationship is definitional, it survives any choice of domain, and the Pareto is
 *    a century-old convention its readers already know how to decode. Locking the bar axis to
 *    `0 → total` instead — so that 100% and the total are one ruler — buys a formal purity and pays
 *    for it in the chart's primary job. A single bucket is a small fraction of the total by
 *    definition, so every bar ends up crushed into the left of the plot and bucket-to-bucket
 *    comparison, the thing the reader came for, is the thing that gets sacrificed.
 *
 *    What that costs is worth naming plainly: a bar's length is now its size relative to the
 *    *fullest bucket*, not its share of everything. "What fraction is this?" is no longer legible
 *    from a bar, which makes the cumulative line and the tooltip the only two places that answer
 *    it — neither is decoration and neither is optional. For the same reason the axes are ticked
 *    separately: shared tick positions would draw an alignment that no longer exists, and a grid
 *    line standing for two values at once misleads worse than one standing for none.
 *
 * 3. **The line is annotation, not a second series.** It wears `--muted-foreground` (neutral ink),
 *    not a second categorical hue, and it is drawn without dots. A chart-2 line here would read as
 *    a rival measure competing with the bars; a grey one reads as a reference overlaid on them.
 *    Its segments are `linear`, never `monotone` — a smoothed cumulative curve would bulge above or
 *    below the running total between buckets, asserting values that were never measured.
 *
 * 4. **The screen-reader table carries the whole answer.** Both measures and the cumulative share
 *    are in the table for every bucket, so "85% of the transactions are under 1,000, but only 40%
 *    of the money is" is reachable without ever seeing the SVG — which is the sentence this chart
 *    exists to produce.
 */

/**
 * Share-axis ticks: quarters, pinned.
 *
 * Percentages have expected resting points and 0/25/50/75/100 are them, so this axis is spelled
 * out rather than fitted. The measure axis, having no such convention, is left to the chart to
 * tick from whatever range the buckets happen to span.
 */
const SHARE_TICKS: number[] = [0, 25, 50, 75, 100];

/** Category-axis gutter. Sized for a grouped two-sided money range (`1,000,000–2,000,000`). */
const CATEGORY_AXIS_WIDTH = 112;

/** One bucket as supplied by the caller. */
interface AmountBucket {
  /** Human-readable range, e.g. `1,000–2,000`. */
  label: string;
  /** Number of successful transactions in the bucket. */
  count: number;
  /** Summed amount of those transactions, in major units. */
  volume: number;
}

/**
 * Every piece of text this chart renders, already translated.
 *
 * The components in `components/charts` are deliberately i18n-agnostic — they never call
 * `useTranslation`, they take finished strings the way `description` is taken. That is not
 * squeamishness about hooks: it keeps the charts renderable from a test, a story or a future
 * PDF/email path where no i18n provider is mounted, and it keeps the locale files reachable from
 * the page that owns the feature rather than scattered across the presentation layer.
 *
 * Three of these fields are *already resolved against `measure`* by the caller, and that
 * asymmetry is the deliberate part. A component that branched internally — picking the word
 * "transactions" or "volume" and splicing it into a header — would be asserting that "cumulative
 * share of X" is a fixed English frame with a hole in it. It is not: word order, classifiers and
 * particles all move in Thai, so the only translatable unit is the whole sentence. The caller holds
 * `measure`, so it picks the finished string and this component just prints it.
 */
export interface AmountHistogramLabels {
  /** Category column header for the text equivalent, e.g. "Amount range". */
  amountRange: string;
  /** The count measure's name — a column header and a tooltip row, e.g. "Transactions". */
  transactions: string;
  /** The volume measure's name — a column header and a tooltip row, e.g. "Volume". */
  volume: string;
  /** The cumulative line's name — a tooltip row and the series name, e.g. "Cumulative share". */
  cumulativeShare: string;
  /** Cumulative column header, resolved by the caller for `measure`, e.g. "…of transactions". */
  cumulativeShareOfMeasure: string;
  /** The bar series' name, resolved by the caller for `measure`, e.g. "Transactions". */
  measureName: string;
  /** Bar legend entry, resolved by the caller for `measure`, e.g. "Transactions per bucket". */
  measurePerBucket: string;
  /** Line legend entry, which has to say which axis reads it, e.g. "Cumulative share (top axis)". */
  cumulativeShareAxis: string;
  /** Shown in place of the plot when the active measure sums to zero. */
  empty: string;
}

/** Props for {@link AmountHistogram}. */
export interface AmountHistogramProps {
  /** One entry per amount bucket, already ordered low → high. */
  data: readonly { label: string; count: number; volume: number }[];
  /** Which measure the bars encode. */
  measure: 'count' | 'volume';
  /** ISO currency code, for formatting volume values. */
  currency: string;
  /** Accessible description. */
  description: string;
  /** Translated chart text. Required, so a new call site cannot silently ship English. */
  labels: AmountHistogramLabels;
  /** Chart height. */
  height?: number | undefined;
}

/** A bucket with the derived values the chart plots. */
interface HistogramRow extends AmountBucket {
  /** `count` or `volume`, whichever `measure` selects — what the bar length encodes. */
  measured: number;
  /** Running share of the active measure accumulated low → high, as a percentage 0–100. */
  cumulative: number;
}

/**
 * Coerce a chart input to a plottable non-negative finite number.
 *
 * The props hand us `number`s. Anything else — a `NaN` from an upstream division, a negative from
 * a refund netted into a bucket — would silently poison the running total and every share derived
 * from it, so it is floored to zero here rather than propagated into the axis domain.
 */
function plottable(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * Render a `volume` as the decimal string the money formatters require.
 *
 * `formatMoney` takes a `Money`, whose `currency` is the narrow `Currency` enum; this
 * component's `currency` prop is a plain `string`, so we call the same function's underlying
 * `formatDecimalString`/`formatCompactAmount`, which are string-typed all the way down.
 *
 * The lossy step is the prop shape, not this function: `volume` is already a JS float by the time
 * it reaches us, so exactness is gone before we are called (see `lib/money.ts` on why the rest of
 * the console never does this). Fixing the axis to the currency's own scale at least stops a float
 * artefact — `4212.000000000001` — reaching a pixel.
 */
function volumeToDecimalString(value: number, currency: string): string {
  const safe = plottable(value);
  if (safe === 0) return '0';
  // `toFixed` switches to exponential notation at 1e21, which the decimal-string parser rejects.
  if (safe >= 1e21) return BigInt(Math.trunc(safe)).toString();
  return safe.toFixed(decimalPlacesFor(currency));
}

/**
 * Derive the plotted rows and the total of the active measure.
 *
 * The cumulative share is computed from the same running sum that produces `total`, in the same
 * order, so the last bucket lands on exactly `100` rather than `99.99999999999999`. An empty or
 * all-zero input yields `total === 0`, and every share is then reported as `0` — no division by
 * zero, no `NaN` in the axis domain.
 */
function buildRows(
  data: readonly AmountBucket[],
  measure: 'count' | 'volume',
): { rows: HistogramRow[]; total: number } {
  const measured = data.map((bucket) =>
    plottable(measure === 'count' ? bucket.count : bucket.volume),
  );
  const total = measured.reduce((sum, value) => sum + value, 0);

  let running = 0;
  const rows = data.map((bucket, index): HistogramRow => {
    running += measured[index] ?? 0;
    return {
      label: bucket.label,
      count: plottable(bucket.count),
      volume: plottable(bucket.volume),
      measured: measured[index] ?? 0,
      cumulative: total > 0 ? (running / total) * 100 : 0,
    };
  });

  return { rows, total };
}

/** Format a cumulative share for display. One decimal, because 99.4% is not 99%. */
function formatShare(value: number): string {
  return `${value.toFixed(1)}%`;
}

/**
 * The text equivalent, shared by the populated and empty renders.
 *
 * Carries both measures plus the cumulative share, so the table answers the question on its own
 * rather than restating whichever measure happens to be selected.
 */
function HistogramTable({
  rows,
  currency,
  description,
  labels,
}: {
  rows: readonly HistogramRow[];
  currency: string;
  description: string;
  labels: AmountHistogramLabels;
}): ReactNode {
  return (
    // `sr-only` goes on a wrapping `div`, never on the `<table>`. See rule 6 in `charts.tsx`.
    <div className="sr-only">
      <table>
        <caption>{description}</caption>
        <thead>
          <tr>
            <th scope="col">{labels.amountRange}</th>
            <th scope="col">{labels.transactions}</th>
            <th scope="col">{labels.volume}</th>
            <th scope="col">{labels.cumulativeShareOfMeasure}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              <td>{Math.round(row.count)}</td>
              <td>
                {formatDecimalString(volumeToDecimalString(row.volume, currency), currency, {
                  withCode: true,
                })}
              </td>
              <td>{formatShare(row.cumulative)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Tooltip payload row, as Recharts hands it over. */
interface TooltipEntry {
  payload?: HistogramRow | undefined;
}

/**
 * Tooltip for one bucket.
 *
 * It reports *both* measures and the cumulative share regardless of which measure the bars encode.
 * The whole point of the chart is the gap between the two ("most of the transactions, a minority of
 * the money"), and a tooltip that echoed only the active measure would hide half of it.
 */
function HistogramTooltip({
  active,
  payload,
  measure,
  currency,
  labels,
}: {
  active?: boolean | undefined;
  payload?: readonly TooltipEntry[] | undefined;
  measure: 'count' | 'volume';
  currency: string;
  labels: AmountHistogramLabels;
}): ReactNode {
  const row = active ? payload?.[0]?.payload : undefined;
  if (!row) return null;

  // `measure` survives here (unlike in the table) because it decides *emphasis*, not wording:
  // which of the two rows is inked as the one the bars are drawn from. That is a styling
  // decision, and styling does not translate.
  const lines: { name: string; value: string; emphasis: boolean }[] = [
    {
      name: labels.transactions,
      value: String(Math.round(row.count)),
      emphasis: measure === 'count',
    },
    {
      name: labels.volume,
      value: formatDecimalString(volumeToDecimalString(row.volume, currency), currency, {
        withSymbol: true,
      }),
      emphasis: measure === 'volume',
    },
    { name: labels.cumulativeShare, value: formatShare(row.cumulative), emphasis: false },
  ];

  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-lg">
      <p className="pb-1 font-medium text-popover-foreground">{row.label}</p>
      <ul className="space-y-0.5">
        {lines.map((line) => (
          <li key={line.name} className="flex items-center gap-4 text-muted-foreground">
            <span>{line.name}</span>
            <span
              className={
                line.emphasis
                  ? 'tnum ml-auto font-medium text-popover-foreground'
                  : 'tnum ml-auto text-muted-foreground'
              }
            >
              {line.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A Pareto histogram of successful transaction amounts.
 *
 * Purely presentational: the page owns the count/volume toggle and passes `measure` down, matching
 * the rest of `components/charts`. See the module comment for the scale, colour and ordering
 * decisions.
 */
export function AmountHistogram({
  data,
  measure,
  currency,
  description,
  labels,
  height = 260,
}: AmountHistogramProps): ReactNode {
  // Never sorted: `data` arrives ordered low → high and that order *is* the axis.
  const { rows, total } = buildRows(data, measure);

  const formatMeasure = (value: number): string =>
    measure === 'count'
      ? String(Math.round(value))
      : formatCompactAmount(volumeToDecimalString(value, currency), currency);

  // Nothing to plot: no buckets at all, or buckets whose active measure is uniformly zero. Bars of
  // length zero and a flat 0% line would read as a broken chart rather than as an empty one.
  if (total <= 0) {
    return (
      <figure className="w-full">
        <figcaption className="sr-only">{description}</figcaption>
        <div
          style={{ height }}
          className="flex items-center justify-center rounded-md border border-dashed border-border"
        >
          <p className="text-xs text-muted-foreground">{labels.empty}</p>
        </div>
        {rows.length > 0 ? (
          <HistogramTable
            rows={rows}
            currency={currency}
            description={description}
            labels={labels}
          />
        ) : null}
      </figure>
    );
  }

  return (
    <figure className="w-full">
      <figcaption className="sr-only">{description}</figcaption>
      <div style={{ height }} role="img" aria-label={description}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={rows}
            layout="vertical"
            margin={{ top: 4, right: 12, bottom: 0, left: 0 }}
          >
            {/*
              Dashed to match every other chart in `charts.tsx`, not because dashed grid is the
              better default — it isn't, it adds noise the solid version doesn't. But this chart
              sits on the dashboard beside four siblings that all use `3 3`, and one chart with a
              quieter grid than its neighbours reads as an accident rather than as restraint.
              Consistency wins here; the honest fix is to migrate all five at once, not to start
              with this one.
            */}
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
            {/*
              Fitted to the buckets, not to their sum: the fullest bucket reaches the end of the
              plot and the rest are read against it. `auto` rather than `dataMax` so the axis lands
              on a roundable number a tick can be labelled with — see the module comment for what
              this domain gives up.
            */}
            <XAxis
              type="number"
              domain={[0, 'auto']}
              tickFormatter={formatMeasure}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              allowDecimals={measure === 'volume'}
            />
            {/*
              The line's scale, and its own: 0–100% owes nothing to the domain below it. Nothing but
              the line is bound to it, which is why the legend has to say which axis it reads —
              there is no longer any position where the two agree by construction.
            */}
            <XAxis
              xAxisId="share"
              type="number"
              orientation="top"
              domain={[0, 100]}
              ticks={SHARE_TICKS}
              tickFormatter={(value: number) => `${Math.round(value)}%`}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              height={18}
            />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={CATEGORY_AXIS_WIDTH}
            />
            <RechartsTooltip
              content={<HistogramTooltip measure={measure} currency={currency} labels={labels} />}
              cursor={{ fill: 'hsl(var(--muted) / 0.4)' }}
            />
            <Bar
              dataKey="measured"
              name={labels.measureName}
              fill="hsl(var(--chart-1))"
              // Rounded data-end, square at the baseline; capped so the band keeps some air.
              radius={[0, 4, 4, 0]}
              maxBarSize={24}
              isAnimationActive={false}
            />
            <Line
              xAxisId="share"
              type="linear"
              dataKey="cumulative"
              name={labels.cumulativeShare}
              stroke="hsl(var(--muted-foreground))"
              strokeWidth={2}
              strokeLinecap="round"
              dot={false}
              activeDot={{
                r: 4,
                fill: 'hsl(var(--muted-foreground))',
                // A 2px surface ring keeps the marker legible where it crosses a bar.
                stroke: 'hsl(var(--card))',
                strokeWidth: 2,
              }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {/*
        Two marks, so a legend is not optional. The key shapes differ as well as the colours — a
        dot for the bars, a rule for the line — so identity survives without colour.
      */}
      <ul className="flex flex-wrap gap-4 pt-2 text-2xs text-muted-foreground">
        <li className="flex items-center gap-1.5">
          <span
            className="size-2 rounded-full"
            style={{ backgroundColor: 'hsl(var(--chart-1))' }}
            aria-hidden="true"
          />
          {labels.measurePerBucket}
        </li>
        <li className="flex items-center gap-1.5">
          <span
            className="h-0.5 w-3 rounded-full"
            style={{ backgroundColor: 'hsl(var(--muted-foreground))' }}
            aria-hidden="true"
          />
          {labels.cumulativeShareAxis}
        </li>
      </ul>
      {/* Text equivalent — the chart is decorative once this table exists. */}
      <HistogramTable rows={rows} currency={currency} description={description} labels={labels} />
    </figure>
  );
}
